import { put } from 'redux-saga/effects';
import { ContractsAndConstructionsService } from '../../../services/ContractsAndConstructions';
import { CustomersService } from '../../../services/Customers';
import { loadCustomerFromIdSuccess } from '../customers/actions';
import { ICustomer } from '../customers/types';
import { loadContracts } from '../fabrication-contracts/actions';
import {
  EWeekDays,
  IFabricationContract
} from '../fabrication-contracts/types';
import { ECustomerTypeView } from '../user/types';
import {
  sendConstructionRegistrationSuccess,
  sendConstructionRegistrationFailure,
  loadCustomerContracts,
  loadCustomerContractsSuccess,
  loadCustomerContractsFailure
} from './actions';
import { Types, IConstructionRegistrationState } from './types';
import { sendTagging } from '../tagging/actions';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';
import { handleCopyAndEmail } from '../../../utils/formatStrings';

const DAY_LABELS: Record<EWeekDays, string> = {
  [EWeekDays.MONDAY]: 'segunda',
  [EWeekDays.TUESDAY]: 'terca',
  [EWeekDays.WEDNESDAY]: 'quarta',
  [EWeekDays.THURSDAY]: 'quinta',
  [EWeekDays.FRIDAY]: 'sexta',
  [EWeekDays.SATURDAY]: 'sabado',
  [EWeekDays.SUNDAY]: 'domingo'
};

interface ISendConstructionRegistrationSagaParams {
  payload: {
    constructionRegistration: IConstructionRegistrationState;
    selectedCustomerId: string;
    customerViewType: ECustomerTypeView;
  };
  type: Types;
}

interface ILoadCustomerContractsSagaParams {
  payload: {
    selectedCustomerId: string;
    customerViewType: ECustomerTypeView;
    needToLoadCustomer: boolean;
  };
  type: Types;
}

interface ILoadCustomerContractsSagaParamsV2 {
  payload: {
    selectedCustomerId: string;
    needToLoadCustomer: boolean;
  };
  type: Types;
}

export function* sendConstructionRegistrationSaga(
  params: ISendConstructionRegistrationSagaParams
) {
  const { constructionRegistration, selectedCustomerId, customerViewType } =
    params.payload;
  try {
    const contractsAndConstructionService =
      new ContractsAndConstructionsService();
    yield contractsAndConstructionService.sendConstructionRegistration(
      constructionRegistration
    );
    yield put(sendConstructionRegistrationSuccess());

    const { constructionData, deliveryConditions, responsiblesOfConstruction } =
      constructionRegistration;
    const horarios = (deliveryConditions?.allowedDaysAndTimeToReceive ?? [])
      .filter((d) => d.checked)
      .map((d) => ({
        dia: DAY_LABELS[d.day],
        inicio: d.startTime ?? '',
        fim: d.endTime ?? ''
      }));
    yield put(
      sendTagging({
        event: 'success',
        screenName:
          'LG:ContratosEObras:CadastroDeObra:RevisaoDoCadastro:CadastroRealizadoComSucesso',
        itemName: 'success:obra_cadastrada_com_sucesso',
        itemDescription:
          'contratosEObras_cadastroDeObra_revisaoDoCadastro_obraCadastradaComSucesso',
        nonInteraction: true,
        extraData: {
          status_obra: 'Aberta',
          cidade: constructionData?.address.city ?? '',
          estado: constructionData?.address.state ?? '',
          tipo_veiculo: (deliveryConditions?.allowedVehicleTypes ?? []).join(
            ','
          ),
          tipo_descarga: deliveryConditions?.allowedDischargeType ?? '',
          horarios_recebimento: horarios,
          qtd_responsaveis: responsiblesOfConstruction.length
        }
      })
    );

    yield put(loadCustomerContracts(selectedCustomerId, customerViewType)); // atualiza os contratos na Gestão de contratos
    yield put(loadContracts(customerViewType)); // atualiza os contratos em fabrication-contracts
  } catch (err) {
    console.error('sendConstructionRegistrationSaga', err);

    yield put(
      sendTagging({
        screenName:
          'LG:ContratosEObras:CadastroDeObra:RevisaoDoCadastro:FalhaNoCadastroDaObra',
        event: 'error',
        itemName: 'error:erro_inesperado_ao_cadastrar_a_obra',
        itemDescription:
          'contratosEObras:cadastroDeObra:revisaoDoCadastro:errorErroInesperadoAoCadastrarAObra',
        nonInteraction: true
      })
    );

    yield put(
      addToast({
        message:
          'Ocorreu um erro inesperado, tente novamente em alguns instantes. Se o problema persistir, entre em contato com o suporte',
        type: EToastTypes.DANGER,
        labelActionOne: 'CONTATAR SUPORTE',
        handleClickActionOne: handleCopyAndEmail,
        durationInMS: 5000
      })
    );
    yield put(sendConstructionRegistrationFailure(err));
  }
}

export function* loadCustomerContractsSaga(
  params: ILoadCustomerContractsSagaParams
) {
  const { selectedCustomerId, customerViewType, needToLoadCustomer } =
    params.payload;

  if (needToLoadCustomer) {
    try {
      const customerService = new CustomersService();
      const customer: ICustomer = yield customerService.getCustomerById(
        selectedCustomerId
      );
      yield put(loadCustomerFromIdSuccess(customer));
    } catch (err) {
      console.error('loadCustomerContractsSaga', err);
      yield put(loadCustomerContractsFailure(err, true));
      return;
    }
  }

  try {
    const contractsAndConstructionService =
      new ContractsAndConstructionsService();
    const customerContracts: IFabricationContract[] =
      yield contractsAndConstructionService.getCustomerContracts(
        selectedCustomerId,
        customerViewType
      );
    yield put(loadCustomerContractsSuccess(customerContracts));
  } catch (err) {
    console.error('loadCustomerContractsSaga', err);
    yield put(
      addToast({
        type: EToastTypes.DANGER,
        message: 'Falha ao carregar os contratos.'
      })
    );
    yield put(loadCustomerContractsFailure(err));
  }
}

export function* loadCustomerContractsSagaV2(
  params: ILoadCustomerContractsSagaParamsV2
) {
  const { selectedCustomerId, needToLoadCustomer } = params.payload;

  if (needToLoadCustomer) {
    try {
      const customerService = new CustomersService();
      const customer: ICustomer = yield customerService.getCustomerById(
        selectedCustomerId
      );
      yield put(loadCustomerFromIdSuccess(customer));
    } catch (err) {
      console.error('loadCustomerContractsSagaV2', err);
      yield put(loadCustomerContractsFailure(err, true));
      return;
    }
  }

  try {
    const contractsAndConstructionService =
      new ContractsAndConstructionsService();
    const customerContracts: IFabricationContract[] =
      yield contractsAndConstructionService.getCustomerContractsV2(
        selectedCustomerId
      );
    yield put(loadCustomerContractsSuccess(customerContracts));
  } catch (err) {
    console.error('loadCustomerContractsSagaV2', err);
    yield put(
      addToast({
        type: EToastTypes.DANGER,
        message: 'Falha ao carregar os contratos.'
      })
    );
    yield put(loadCustomerContractsFailure(err));
  }
}
